import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { fetchMe, signOut as apiSignOut } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const me = await fetchMe();
            setUser(me);
            return me;
        } catch {
            setUser(null);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // If any API call returns 401, our session has been invalidated server-side
    // (expired, deleted, or rotated). Clear the cached user immediately so the
    // UI flips to logged-out state without waiting for a manual refresh.
    useEffect(() => {
        const handler = () => setUser(null);
        window.addEventListener("lendly:auth-expired", handler);
        return () => window.removeEventListener("lendly:auth-expired", handler);
    }, []);

    const logout = useCallback(async () => {
        try {
            await apiSignOut();
        } catch {
            /* ignore */
        }
        setUser(null);
    }, []);

    const value = useMemo(
        () => ({
            user,
            loading,
            isAuthenticated: user !== null,
            profileComplete: !!user?.profile_complete,
            refresh,
            setUser,
            logout,
        }),
        [user, loading, refresh, logout],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return ctx;
}
